import React, { createRef, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, type Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  DebouncedInlineSearch,
  type InlineSearchHandle,
  TextWithTooltip,
} from '@pbt/pbt-ui-components'

import PuiSwitch from '~/components/common/PuiSwitch'
import {
  type KibSearchHandle,
  KibSearchWrapper,
} from '~/components/common/search/KibSearchWrapper'
import FeatureToggle from '~/constants/featureToggle'
import { applyPatientRestrictions } from '~/store/actions/orders'
import {
  getFeatureToggle,
  getSearchCatalogFiltersAndFacetsEnabled,
} from '~/store/reducers/constants'
import {
  getAppliedPatientRestrictions,
  getOrdersSearchResults,
  getOrdersSearchResultsTotalCount,
} from '~/store/reducers/orders'
import {
  getAppointmentId,
  getClientId,
  getOrderFilters,
  getPatientId,
  getSoapId,
} from '~/store/reducers/soap'
import type { BaseChargeTabProps } from '~/types/entities/charges'
import {
  SearchChargeNoSelectionReason,
  trackEvent,
  TrackingEvent,
  useTrackSearch,
} from '~/utils/trackingProvider'

import { useChargeInlineSearch } from '../../utils/useChargeInlineSearch'
import ChargeSearchResults from './ChargeSearchResults'

const useStyles = makeStyles(
  (theme) => ({
    searchRoot: {
      zIndex: theme.utils.modifyZIndex(theme.zIndex.base, 'above'),
    },
    switch: {
      whiteSpace: 'nowrap',
    },
    switchRoot: {
      marginLeft: 0,
    },
  }),
  { name: 'SearchChargeTab' },
)

export interface SearchChargeTabProps extends BaseChargeTabProps {
  clearSearch: () => void
  patientId?: string
  searchRef: React.Ref<InlineSearchHandle | KibSearchHandle> | undefined
}

const SearchChargeTab = ({
  clearSearch,
  disabled,
  handleEditListItem,
  handleRefill,
  handleSavePrescription,
  logItemStateGetter,
  openPrescriptionDialog,
  searchRef,
  patientId: patientIdProp,
}: SearchChargeTabProps) => {
  const classes = useStyles()

  const dispatch = useDispatch()

  const { t } = useTranslation(['Search', 'Soap', 'Tooltips'])

  const appointmentId = useSelector(getAppointmentId)
  const soapId = useSelector(getSoapId)
  const clientId = useSelector(getClientId)
  const orderFilters = useSelector(getOrderFilters)
  const soapPatientId = useSelector(getPatientId)
  const searchResults = useSelector(getOrdersSearchResults)
  const searchResultsTotalCount = useSelector(getOrdersSearchResultsTotalCount)
  const appliedPatientRestrictions = useSelector(getAppliedPatientRestrictions)
  const isSearchCatalogFiltersAndFacetsEnabled = useSelector(
    getSearchCatalogFiltersAndFacetsEnabled,
  )
  const isAwoChargesR5Epic5Enabled = useSelector(
    getFeatureToggle(FeatureToggle.AWO_CHARGES_R5_5),
  )

  const { addSearchAttempt, trackSearch, startSearch } = useTrackSearch(
    TrackingEvent.SEARCH_CHARGE_TAB,
    'selectedItemVariationId',
  )

  const isMedium = useMediaQuery<Theme>((theme) => theme.breakpoints.down('lg'))

  const itemsRefs = Array.from({ length: searchResults.length }, () =>
    createRef<any>(),
  )

  const validate = () =>
    itemsRefs.every((ref) =>
      ref.current?.validate ? ref.current.validate() : true,
    )

  const patientId = soapPatientId || patientIdProp

  const {
    onSearchChange,
    onSearchBlur,
    onSearchFocus,
    searchDisabled,
    searchTerm,
  } = useChargeInlineSearch({
    validate,
    patientId,
    onSearch: addSearchAttempt,
    onFocus: startSearch,
  })

  // Use refs to store tracked values to avoid tracking when search term changes
  const searchTermRef = useRef(searchTerm)
  const searchResultsTotalCountRef = useRef(searchResultsTotalCount)
  const hasSelectedItemRef = useRef(false)

  const onSelect = useCallback(
    (itemId: string | undefined, props: any) => {
      hasSelectedItemRef.current = true
      trackSearch(itemId, {
        ...props,
        soapId,
        appointmentId,
      })
    },
    [trackSearch, soapId, appointmentId],
  )

  const onSearch = useCallback(
    (value: string) => {
      if (searchTerm && !hasSelectedItemRef.current) {
        trackEvent(TrackingEvent.SEARCH_CHARGE_NO_SELECTION, {
          searchTerm,
          resultsCount: searchResultsTotalCountRef.current,
          reason: value
            ? SearchChargeNoSelectionReason.SEARCH_TERM_UPDATED
            : SearchChargeNoSelectionReason.SEARCH_TERM_CLEARED,
        })
      }
      onSearchChange(value)
    },
    [onSearchChange, searchTerm],
  )

  useEffect(() => {
    searchTermRef.current = searchTerm
    hasSelectedItemRef.current = false
  }, [searchTerm])

  useEffect(() => {
    searchResultsTotalCountRef.current = searchResultsTotalCount
  }, [searchResultsTotalCount])

  // Track no-selection when component unmounts
  useEffect(
    () => () => {
      if (searchTermRef.current && !hasSelectedItemRef.current) {
        trackEvent(TrackingEvent.SEARCH_CHARGE_NO_SELECTION, {
          searchTerm: searchTermRef.current,
          resultsCount: searchResultsTotalCountRef.current,
          reason: SearchChargeNoSelectionReason.NAVIGATED_AWAY,
        })
      }
    },
    [],
  )

  return (
    <Grid
      container
      p={isAwoChargesR5Epic5Enabled ? 0 : 2}
      pb={isAwoChargesR5Epic5Enabled ? 1 : undefined}
    >
      <Grid
        container
        item
        alignItems="baseline"
        width="100%"
        wrap={isMedium ? 'wrap-reverse' : 'nowrap'}
        zIndex={1}
      >
        <Grid container position="relative">
          {isSearchCatalogFiltersAndFacetsEnabled ? (
            <KibSearchWrapper
              disabled={disabled || searchDisabled}
              placeholder={t('Search:SEARCH_TO_ADD')}
              ref={searchRef as React.Ref<KibSearchHandle>}
              showCloseButton={Boolean(searchTerm)}
              value={searchTerm}
              onBlur={onSearchBlur}
              onChange={onSearch}
              onClear={clearSearch}
              onFocus={onSearchFocus}
            />
          ) : (
            <DebouncedInlineSearch
              classes={{ root: classes.searchRoot }}
              disabled={disabled || searchDisabled}
              initialValue={searchTerm}
              placeholder={t('Search:SEARCH_TO_ADD')}
              ref={searchRef as React.Ref<InlineSearchHandle>}
              showCloseButton={Boolean(searchTerm)}
              onBlur={onSearchBlur}
              onChange={onSearch}
              onFocus={onSearchFocus}
            />
          )}
          {patientId && clientId && (
            <ChargeSearchResults
              appointmentId={appointmentId}
              clearSearch={clearSearch}
              clientId={clientId}
              disabled={disabled}
              handleEditListItem={handleEditListItem}
              handleRefill={handleRefill}
              handleSavePrescription={handleSavePrescription}
              itemsRefs={itemsRefs}
              logItemStateGetter={logItemStateGetter}
              openPrescriptionDialog={openPrescriptionDialog}
              orderFilters={orderFilters}
              patientId={patientId}
              searchTerm={searchTerm}
              onSearch={onSearch}
              onSelect={onSelect}
            />
          )}
        </Grid>
        {!isSearchCatalogFiltersAndFacetsEnabled && (
          <Grid width={396}>
            <PuiSwitch
              checked={appliedPatientRestrictions}
              classes={{
                root: classes.switchRoot,
                switchLabel: classes.switch,
              }}
              label={
                <TextWithTooltip
                  tooltipText={t('Tooltips:ORDER_LIST_WITH_FILTERS_TOGGLE')}
                  variant="body2"
                >
                  {t('Soap:LIST_WITH_FILTERS.ORDER_LIST_WITH_FILTERS_LABEL')}
                </TextWithTooltip>
              }
              onChange={() =>
                dispatch(applyPatientRestrictions(!appliedPatientRestrictions))
              }
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

export default SearchChargeTab
